import { computed, h, defineComponent, ref } from 'vue';
import { inputField } from '@/ui/components/form';
import { Button } from '@/ui/components/Button';
import { box } from '@/ui/components';
import { ContainsTranslationProps } from '@/ui/components/activityLogs/types';
import { Activities as ActivitiesCodecs, ActivityCreated } from '@/entities/activity/model';
import throttle from 'lodash/throttle';
import { t } from '@/ui/i18n/i18n';
import { LogComponentCreator } from '@/ui/components/activityLogs/factory/LogComponentCreator';
import { ActivityFields } from '@/entities/activity/constants';

interface LogsProps extends ContainsTranslationProps {
  logs: ActivitiesCodecs[];
  showCreateComment?: boolean;
  resourceName: string;
  componentCreator: LogComponentCreator;
  customClasses?: Array<string>;
}

export const ActivityLogs = defineComponent<LogsProps>({
  name: 'ActivityLogs',
  props: ['logs', 'showCreateComment', 'valuesTranslation', 'fieldsTranslation',
    'resourceName', 'componentCreator', 'customClasses'],
  setup(props, context) {
    const comment = ref('');
    const onlyAllowedLogs = computed(() => props.logs.filter(row => row.field !== ActivityFields.reportedAt));

    const onCommentInput = (value: string) => {
      comment.value = value;
    };

    const onSendComment = () => {
      if (comment.value) {
        context.emit('comment:created', comment.value);
        comment.value = '';
      }
    };

    const classes = computed(() => ([
      'activity-logs',
      ...(props.showCreateComment ? ['activity-logs--has-comment-section'] : []),
    ]));

    const isButtonDisabled = computed(() => !comment.value);

    const onSeeDetailsClick = (log: ActivityCreated) => {
      context.emit('see-details:click', log);
    };

    const offsetBottom = 200;

    const onScroll = (e: Event) => {
      const { scrollTop, clientHeight, scrollHeight } = e.target as Element;

      if (scrollTop + clientHeight >= scrollHeight - offsetBottom) {
        context.emit('scroll:bottom');
      }
    };

    const onScrollDebounce = throttle(onScroll, 500);

    return function render() {
      return h('div', { class: classes.value }, [
        props.showCreateComment
          ? h('div', { class: 'activity-logs__create-input-row' }, [
            inputField({
              value: comment.value,
              name: 'comment',
              label: '',
              hideLabel: true,
              required: false,
              placeholder: t('activity-logs.comment-placeholder'),
              disabled: false,
              errors: [],
              autocomplete: 'off',
            }, onCommentInput, {
              'input:enter': onSendComment,
            }),
            h(Button, {
              class: 'ml-2',
              props: { disabled: isButtonDisabled.value },
              on: { click: onSendComment },
            }, t('activity-logs.send')),
          ]) : null,
        box([
          onlyAllowedLogs.value.length > 0 && onlyAllowedLogs.value.map(
            log => h(props.componentCreator.logEntityComponentFactory(log.activity), {
              props: {
                log,
                valuesTranslation: props.valuesTranslation,
                fieldsTranslation: props.fieldsTranslation,
                resourceName: props.resourceName,
              },
              on: {
                'see-details:click': onSeeDetailsClick,
              },
            }),
          ),
          context.slots.afterList ? context.slots.afterList() : null,
        ], {
          class: ['activity-logs__box', ...(props.customClasses ?? [])],
          nativeOn: { scroll: onScrollDebounce },
        }),
      ]);
    };
  },
});
